import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Check, Lock } from "lucide-react";

/**
 * Self-playing, looping demo: the "synapse" sidecar extension transfers a finished
 * note from Notation into a third-party EMR ("AnyEMR") — fields auto-fill and the
 * record scrolls Subjective → Assessment → Plan. No user interaction.
 */

interface Field {
  section: "Subjective" | "Assessment" | "Plan";
  label: string;
  value: string;
}

const FIELDS: Field[] = [
  { section: "Subjective", label: "Chief Complaint", value: "R shoulder pain, overhead tasks" },
  { section: "Subjective", label: "PLOF", value: "Independent, no limitations" },
  { section: "Subjective", label: "Pain", value: "6/10 with reaching" },
  { section: "Assessment", label: "Clinical Impression", value: "R shoulder impingement" },
  { section: "Assessment", label: "Rehab Potential", value: "Very Good" },
  { section: "Plan", label: "Goals", value: "↑ flexion AROM to 150°" },
  { section: "Plan", label: "Frequency", value: "2x / week × 6 weeks" },
];

const SECTIONS: Field["section"][] = ["Subjective", "Assessment", "Plan"];
const TOTAL = FIELDS.length;

const STEP_MS = 850;
const HOLD_MS = 2600;

export default function InstantEmrWidget() {
  // 0..TOTAL fields written; TOTAL === complete.
  const [written, setWritten] = useState(0);
  const timeoutRef = useRef<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [offset, setOffset] = useState(0);

  const activeSection = FIELDS[Math.min(written, TOTAL - 1)].section;
  const complete = written >= TOTAL;

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setWritten(TOTAL);
      return;
    }
    const isLast = written >= TOTAL;
    timeoutRef.current = window.setTimeout(
      () => setWritten((w) => (w >= TOTAL ? 0 : w + 1)),
      isLast ? HOLD_MS : STEP_MS
    );
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, [written]);

  useEffect(() => {
    const el = sectionRefs.current[activeSection];
    if (el) setOffset(el.offsetTop);
  }, [activeSection]);

  return (
    <div className="relative w-full h-full font-productSans">
      {/* AnyEMR — wide record card */}
      <div className="absolute left-0 top-[5%] bottom-[5%] w-[62%] rounded-[24px] bg-white shadow-[0_20px_56px_-18px_rgba(31,61,59,0.42)] overflow-hidden flex flex-col z-10">
        {/* Neutral third-party EMR header */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-[#334155]">
          <span className="flex items-center gap-1.5 text-[12px] font-bold text-white">
            <span className="w-3.5 h-3.5 rounded-[5px] bg-white/90" />
            AnyEMR
          </span>
          <span className="flex items-center gap-1 text-[9px] text-white/70">
            <Lock className="w-2.5 h-2.5" />
            Patient-001
          </span>
        </div>

        {/* Scrolling record body */}
        <div ref={scrollRef} className="flex-1 min-h-0 overflow-hidden px-3.5">
          <motion.div
            className="relative pt-3"
            animate={{ y: -offset }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            {SECTIONS.map((section) => (
              <div
                key={section}
                ref={(el) => (sectionRefs.current[section] = el)}
                className="mb-4"
              >
                <p
                  className={`text-[10.5px] font-bold uppercase tracking-wide mb-1.5 transition-colors duration-300 ${
                    section === activeSection && !complete
                      ? "text-[#1F3D3B]"
                      : "text-gray-400"
                  }`}
                >
                  {section}
                </p>
                <div className="flex flex-col gap-1.5">
                  {FIELDS.map((field) => {
                    if (field.section !== section) return null;
                    const gi = FIELDS.indexOf(field);
                    const filled = gi < written;
                    const justFilled = gi === written - 1;
                    return (
                      <div key={field.label}>
                        <p className="text-[8.5px] text-gray-400 mb-0.5">
                          {field.label}
                        </p>
                        <motion.div
                          animate={{
                            boxShadow: justFilled
                              ? "0 0 0 2px rgba(72,102,51,0.5)"
                              : "0 0 0 0px rgba(72,102,51,0)",
                          }}
                          transition={{ duration: 0.4 }}
                          className={`flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[9.5px] min-h-[26px] ${
                            filled
                              ? "bg-[#f6faf7] text-gray-800"
                              : "bg-gray-50 text-transparent"
                          }`}
                        >
                          {filled ? (
                            <>
                              <span className="flex-1 leading-snug">
                                {field.value}
                              </span>
                              <Check className="w-3 h-3 text-[#486633] shrink-0" />
                            </>
                          ) : (
                            <span className="w-full h-1.5 rounded bg-gray-200" />
                          )}
                        </motion.div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* synapse — vertical sidecar extension */}
      <div className="absolute right-0 top-[9%] bottom-[9%] w-[42%] rounded-[24px] bg-white shadow-[0_24px_64px_-18px_rgba(31,61,59,0.45)] overflow-hidden flex flex-col z-20">
        <div className="flex items-center justify-between px-3.5 py-3 bg-[#1F3D3B]">
          <span
            className="text-[16px] text-white leading-none"
            style={{ fontFamily: "'Baloo Bhaina 2', sans-serif" }}
          >
            synapse
          </span>
          <span className="text-[8px] font-medium text-white/70 border border-white/20 rounded-full px-1.5 py-0.5">
            EMR Assist
          </span>
        </div>

        <div className="flex-1 flex flex-col px-3.5 py-3">
          <p className="text-[9px] font-semibold text-gray-500 mb-2">
            Execute mapping
          </p>

          {/* Live status */}
          <div className="flex items-center gap-1.5 mb-3">
            <motion.span
              className={`w-2 h-2 rounded-full ${
                complete ? "bg-[#166534]" : "bg-[#7DD3FC]"
              }`}
              animate={complete ? { scale: 1 } : { opacity: [1, 0.3, 1] }}
              transition={{ duration: 1.2, repeat: complete ? 0 : Infinity }}
            />
            <span className="text-[10.5px] font-semibold text-gray-800">
              {complete ? "Transfer complete" : "Transferring…"}
            </span>
          </div>

          {/* Progress bar */}
          <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden mb-3">
            <motion.div
              className="h-full rounded-full bg-[#486633]"
              animate={{ width: `${(written / TOTAL) * 100}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
          </div>

          {/* Stats */}
          <div className="rounded-xl bg-gray-50 px-3 py-2.5 flex flex-col gap-1.5 text-[10px]">
            <Stat label="Fields planned" value={TOTAL} />
            <Stat label="Fields written" value={written} highlight />
            <Stat label="Fields skipped" value={0} />
          </div>

          <div className="mt-auto pt-3">
            <div
              className={`w-full text-center rounded-lg py-2 text-[10px] font-semibold transition-colors duration-300 ${
                complete
                  ? "bg-[#1F3D3B] text-white"
                  : "bg-gray-100 text-gray-400"
              }`}
            >
              {complete ? "View activity log" : "Mapping fields…"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  highlight,
}: {
  label: string;
  value: number;
  highlight?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-gray-500">{label}</span>
      <span
        className={`font-bold ${highlight ? "text-[#486633]" : "text-gray-800"}`}
      >
        {value}
      </span>
    </div>
  );
}
